// chainDocSanitize.ts

/**
 * Take the chain's own marks back out of a report before it is stored.
 *
 * The `chainField` mark exists to give a measurement an identity while the report is
 * open. It is not clinical content, and it must never end up in the saved document —
 * TipTap validates stored JSON against the running schema, so a report saved with the
 * mark cannot be opened by any build that does not define it. That is not hypothetical:
 * a report saved on this branch opened blank on a staging build with
 * "RangeError: There is no mark type chainField in this schema", and an empty editor plus
 * autosave is how a report body gets wiped.
 *
 * Nothing is lost by stripping them. `backfillChainMarks` re-applies every mark from the
 * labels in the document each time a report is opened — that is exactly why chaining
 * works on reports saved long before the feature existed. The marks are UI state that can
 * always be rebuilt, so they are rebuilt rather than stored.
 *
 * The only casualty is the amber mid-chain warning not surviving a reload. It is advisory
 * and recomputed on the next edit.
 */

const CHAIN_MARK = 'chainField';

/**
 * Strip every `chainField` mark from a ProseMirror JSON document.
 *
 * Returns the input untouched when it is not a JSON document — a brand-new report is
 * still an HTML string at this point, and those carry no marks to remove.
 *
 * Never mutates the input: the live editor keeps its marks, only the copy being stored
 * loses them.
 */
export function stripChainMarks<T>(doc: T): T {
  if (!doc || typeof doc !== 'object') {
    return doc;
  }
  return sanitizeNode(doc as any) as T;
}

function sanitizeNode(node: any): any {
  if (Array.isArray(node)) {
    return node.map(sanitizeNode);
  }
  if (!node || typeof node !== 'object') {
    return node;
  }

  const next: any = { ...node };

  if (Array.isArray(next.marks)) {
    const kept = next.marks.filter((mark: any) => mark?.type !== CHAIN_MARK);
    if (kept.length > 0) {
      next.marks = kept;
    } else {
      // An empty `marks` array is legal but noisy; a node with no marks simply omits it.
      delete next.marks;
    }
  }

  if (Array.isArray(next.content)) {
    next.content = next.content.map(sanitizeNode);
  }

  return next;
}

/** True when the document still carries a chain mark — used by the tests and nothing else. */
export function hasChainMarks(doc: any): boolean {
  if (!doc || typeof doc !== 'object') {
    return false;
  }
  if (Array.isArray(doc)) {
    return doc.some(hasChainMarks);
  }
  if (Array.isArray(doc.marks) && doc.marks.some((m: any) => m?.type === CHAIN_MARK)) {
    return true;
  }
  return Array.isArray(doc.content) ? doc.content.some(hasChainMarks) : false;
}
